import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AILiveDashboard from "./components/aiInterview/AILiveDashboard";
import AIInterviewBadge from "./components/aiInterview/AIInterviewBadge";
import { ErrorBox, LoadingLine, WarningChips, toText } from "./components/aiInterview/aiShared";
import usePolling from "./hooks/usePolling";
import {
  AI_MODE,
  addAINote,
  confirmAIDisclosure,
  fetchAILive,
  fetchAIPlan,
  fetchAISession,
  getAIErrorCode,
  getAIErrorMessage,
  isFinalAIStatus,
  isLiveAIStatus,
  markAIBankQuestion,
} from "./utils/aiInterviewApi";

const LIVE_POLL_MS = 3000;
// Before the bot joins nothing changes quickly, but the page should still notice when it does
const WAITING_POLL_MS = 15000;

// Live dashboard for a "Panelist + AI assistant" interview: /interviews/:interviewId/ai-live
export default function AIInterviewLivePage() {
  const { interviewId } = useParams();
  const [session, setSession] = useState(null);
  const [live, setLive] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planError, setPlanError] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notAvailable, setNotAvailable] = useState("");
  const [pollError, setPollError] = useState("");

  const loadPlan = useCallback(async () => {
    setPlanError("");
    try {
      setPlan(await fetchAIPlan(interviewId));
    } catch (err) {
      setPlanError(getAIErrorMessage(err, "Couldn't load the question bank."));
    }
  }, [interviewId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setNotAvailable("");
    try {
      const next = await fetchAISession(interviewId);
      setSession(next);
      if (!next) {
        setNotAvailable("AI isn't enabled for this interview.");
        return;
      }
      if (next.config?.mode !== AI_MODE.PANELIST_WITH_AI) {
        setNotAvailable("The live dashboard is for Panelist + AI assistant interviews. This interview is run by the AI interviewer.");
        return;
      }
      const [nextLive] = await Promise.all([fetchAILive(interviewId), loadPlan()]);
      setLive(nextLive);
    } catch (err) {
      if (getAIErrorCode(err) === "AI_INTERVIEW_NOT_FOUND") setNotAvailable("AI isn't enabled for this interview.");
      else setError(getAIErrorMessage(err, "Couldn't load the live dashboard."));
    } finally {
      setLoading(false);
    }
  }, [interviewId, loadPlan]);

  useEffect(() => {
    load();
  }, [load]);

  const status = live?.status || session?.status;
  const interval = !live || isFinalAIStatus(status) ? 0 : isLiveAIStatus(status) ? LIVE_POLL_MS : WAITING_POLL_MS;

  usePolling(async () => {
    try {
      setLive(await fetchAILive(interviewId));
      setPollError("");
    } catch (err) {
      setPollError(`${getAIErrorMessage(err, "Lost connection to the AI assistant.")} Retrying…`);
    }
  }, interval, interval > 0);

  const handleDisclosed = async () => {
    const disclosure = await confirmAIDisclosure(interviewId);
    setLive((previous) => ({ ...previous, disclosure }));
  };

  const handleMarkBank = async (questionId, markStatus) => {
    const mark = await markAIBankQuestion(interviewId, questionId, markStatus);
    setLive((previous) => ({
      ...previous,
      bank_marks: {
        ...(previous?.bank_marks || {}),
        [questionId]: { status: mark?.status || markStatus, by: mark?.by, at: mark?.at },
      },
    }));
  };

  const handleAddNote = async (text) => {
    const note = await addAINote(interviewId, { text });
    setLive((previous) => ({ ...previous, notes: [...(previous?.notes || []), note] }));
  };

  return (
    <main className="min-h-screen bg-gray-50 px-3 py-3 sm:px-4 text-black">
      <header className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <h1 className="text-lg font-semibold">Live interview assistant</h1>
          {status && <AIInterviewBadge summary={{ enabled: true, status }} />}
          {session?.config?.round?.name && <span className="text-sm text-gray-600">{toText(session.config.round.name)}</span>}
          {/* Warnings only; scores are never shown during the interview */}
          <WarningChips warnings={session?.warnings} />
        </div>
        <Link to="/interviews" className="text-sm font-medium text-blue-700 underline">All interviews</Link>
      </header>

      {pollError && <p className="mb-2 text-sm text-amber-800" role="status">{pollError}</p>}

      {loading ? (
        <LoadingLine>Loading the live dashboard...</LoadingLine>
      ) : error ? (
        <ErrorBox onRetry={load}>{error}</ErrorBox>
      ) : notAvailable ? (
        <p className="text-sm">{notAvailable}</p>
      ) : live ? (
        <AILiveDashboard
          live={live}
          plan={plan}
          planError={planError}
          onReloadPlan={loadPlan}
          onDisclosed={handleDisclosed}
          onMarkBank={handleMarkBank}
          onAddNote={handleAddNote}
        />
      ) : null}
    </main>
  );
}
